/**
 * Hook principal de Wallet
 * Maneja la creación, importación y login de wallets
 */

import { useState, useEffect } from 'react';
import { useWalletStore } from '@/store/walletStore';
import walletService from '@/services/wallet/wallet.service';
import storageService from '@/services/wallet/storage.service';
import { TransactionSyncService } from '@/services/transactions/transaction-sync.service';

export function useWallet() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { address, isConnected, setAddress, setIsConnected, reset, resetForWalletSwitch } = useWalletStore();

  // Escuchar eventos de wallet lock/unlock
  useEffect(() => {
    const handleWalletLocked = () => {
      console.log('[useWallet] Wallet bloqueada - actualizando estado');
      
      // Detener sincronización de transacciones (el historial queda guardado)
      const syncService = TransactionSyncService.getInstance();
      syncService.stopAutoSync(address);

      reset();

      // Redirigir a la app principal para pedir login de nuevo
      window.location.href = '/';
    };

    const handleWalletUnlocked = (event: CustomEvent) => {
      console.log('[useWallet] Wallet desbloqueada - actualizando estado');
      const { address: unlockedAddress } = event.detail;
      setAddress(unlockedAddress);
      setIsConnected(true);
      
      // Iniciar sincronización de transacciones
      const syncService = TransactionSyncService.getInstance();
      syncService.startAutoSync(unlockedAddress);
    };

    document.addEventListener('wallet:locked', handleWalletLocked);
    document.addEventListener('wallet:unlocked', handleWalletUnlocked as EventListener);

    return () => {
      document.removeEventListener('wallet:locked', handleWalletLocked);
      document.removeEventListener('wallet:unlocked', handleWalletUnlocked as EventListener);
    };
  }, [reset, setAddress, setIsConnected, address]);

  /**
   * Crea una nueva wallet
   */
  const createWallet = async (password: string, name?: string) => {
    try {
      setLoading(true);
      setError(null);

      const result = await walletService.createWallet(password, name);

      setAddress(result.address);
      setIsConnected(true);

      // Iniciar sincronización de transacciones
      const syncService = TransactionSyncService.getInstance();
      syncService.startAutoSync(result.address);

      return result;
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Importa wallet desde clave privada
   */
  const importFromPrivateKey = async (privateKey: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const success = await walletService.importFromPrivateKey(privateKey, password);

      if (success) {
        const walletData = storageService.loadWalletData();
        if (walletData) {
          setAddress(walletData.address);
          setIsConnected(true);
          
          // Iniciar sincronización de transacciones
          const syncService = TransactionSyncService.getInstance();
          syncService.startAutoSync(walletData.address);
        }
      }

      return success;
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Importa wallet desde mnemonic
   */
  const importFromMnemonic = async (mnemonic: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const success = await walletService.importFromMnemonic(mnemonic, password);

      if (success) {
        const walletData = storageService.loadWalletData();
        if (walletData) {
          setAddress(walletData.address);
          setIsConnected(true);
          
          // Iniciar sincronización de transacciones
          const syncService = TransactionSyncService.getInstance();
          syncService.startAutoSync(walletData.address);
        }
      }

      return success;
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Inicia sesión con contraseña
   */
  const login = async (password: string) => {
    try {
      setLoading(true);
      setError(null);

      const success = await walletService.login(password);

      if (success) {
        const walletData = storageService.loadWalletData();
        if (walletData) {
          setAddress(walletData.address);
          setIsConnected(true);
          
          // Iniciar sincronización de transacciones
          const syncService = TransactionSyncService.getInstance();
          syncService.startAutoSync(walletData.address);
        }
      }

      return success;
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cierra sesión
   */
  const logout = () => {
    // Detener sincronización de transacciones (el historial queda guardado)
    const syncService = TransactionSyncService.getInstance();
    syncService.stopAutoSync(address);

    walletService.logout();
    reset();
  };

  /**
   * Verifica si hay sesión activa y wallet desbloqueada
   */
  const hasActiveSession = () => {
    const sessionActive = walletService.hasActiveSession();
    const walletUnlocked = walletService.isWalletUnlocked();
    return sessionActive && walletUnlocked;
  };

  /**
   * Verifica si la wallet está desbloqueada
   */
  const isWalletUnlocked = () => {
    return walletService.isWalletUnlocked();
  };

  /**
   * Bloquea la wallet manualmente
   */
  const lockWallet = () => {
    // Detener sincronización de transacciones (el historial queda guardado)
    const syncService = TransactionSyncService.getInstance();
    syncService.stopAutoSync(address);

    walletService.lockWallet();
  };

  /**
   * Extiende la sesión de la wallet
   */
  const extendSession = () => {
    walletService.extendWalletSession();
  };

  /**
   * Lista todas las wallets de la sesión (para la pantalla de gestión)
   */
  const getAllWallets = () => walletService.getAllWallets();

  /**
   * Cambia de wallet reusando la contraseña de la sesión — no la pide de
   * nuevo. Si devuelve 'wrong_password', la UI debe pedir la contraseña de
   * esa wallet puntual y llamar a switchWalletWithPassword.
   */
  const switchWallet = async (index: number) => {
    try {
      setLoading(true);
      setError(null);
      resetForWalletSwitch();
      return await walletService.switchWalletSeamless(index);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fallback de switchWallet cuando la contraseña cacheada no sirve para
   * esa wallet puntual.
   */
  const switchWalletWithPassword = async (index: number, password: string) => {
    try {
      setLoading(true);
      setError(null);
      resetForWalletSwitch();
      return await walletService.switchWallet(index, password);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Crea o importa una wallet nueva dentro de la sesión ya desbloqueada
   * (sin pedir contraseña) y la deja activa.
   */
  const addWallet = async (
    mode: 'create' | 'import-mnemonic' | 'import-key',
    data: string | undefined,
    name: string
  ) => {
    try {
      setLoading(true);
      setError(null);
      return await walletService.addWallet(mode, data, name);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Elimina una wallet del listado (no permite quedarse en cero).
   */
  const removeWallet = async (address: string) => {
    try {
      setLoading(true);
      setError(null);
      resetForWalletSwitch();
      const result = await walletService.removeWallet(address);
      if (result === 'ok') {
        TransactionSyncService.getInstance().cleanup(address);
      }
      return result;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Renombra una wallet del listado.
   */
  const renameWallet = (walletAddress: string, name: string) => {
    return walletService.renameWallet(walletAddress, name);
  };

  /**
   * Cambia la contraseña de todas las wallets del dispositivo.
   */
  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      setLoading(true);
      setError(null);
      return await walletService.changePassword(oldPassword, newPassword);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    // Estado
    address,
    isConnected,
    loading,
    error,

    // Acciones
    createWallet,
    importFromPrivateKey,
    importFromMnemonic,
    login,
    logout,
    hasActiveSession,
    isWalletUnlocked,
    lockWallet,
    extendSession,

    // Multi-wallet
    getAllWallets,
    switchWallet,
    switchWalletWithPassword,
    addWallet,
    removeWallet,
    renameWallet,
    changePassword,
  };
}

export default useWallet;
